import { useEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent } from 'react';
import type { AgendaEntry, AgendaResponse } from '@aeronexo/contracts';
import {
  calendarDate,
  calendarEventLayout,
  calendarPeriod,
  calendarSlot,
  displayInstant,
  entriesForDay,
  moveCalendarDate,
  moveCalendarMonth,
  scheduleErrors,
  scheduleStates,
  type CalendarView,
} from './schedule-presentation.js';

const hourHeight = 64;
const hours = Array.from({ length: 24 }, (_, hour) => hour);

export interface OperationSchedulePreset {
  readonly plannedDeparture: string;
  readonly plannedArrival: string;
}

function eventTone(entry: AgendaEntry): string {
  if (entry.state === 'cancelled' || entry.cancelledAt) return 'calendarEvent-cancelled';
  if (entry.conflicts.some((conflict) => conflict.hard)) return 'calendarEvent-conflict';
  if (entry.criticalCount > 0 || entry.pendingCount > 0) return 'calendarEvent-attention';
  if (entry.state === 'in_progress') return 'calendarEvent-progress';
  if (entry.state === 'ready') return 'calendarEvent-ready';
  if (entry.state === 'draft') return 'calendarEvent-draft';
  return 'calendarEvent-planned';
}

function dayLabel(day: string) {
  const date = new Date(`${day}T12:00:00Z`);
  return {
    weekday: new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC', weekday: 'short' })
      .format(date)
      .replace('.', ''),
    number: Number(day.slice(8)),
  };
}

function CalendarEvent({
  entry,
  startMinute,
  endMinute,
  lane,
  laneCount,
  timeZone,
  onOpen,
}: {
  readonly entry: AgendaEntry;
  readonly startMinute: number;
  readonly endMinute: number;
  readonly lane: number;
  readonly laneCount: number;
  readonly timeZone: string;
  readonly onOpen: (id: string) => void;
}) {
  const start = entry.revisedDeparture ?? entry.plannedDeparture;
  const end = entry.revisedArrival ?? entry.plannedArrival;
  const style = {
    top: `${(startMinute / 60) * hourHeight + 2}px`,
    height: `${Math.max(30, ((endMinute - startMinute) / 60) * hourHeight - 4)}px`,
    left: `calc(${(lane / laneCount) * 100}% + 4px)`,
    width: `calc(${100 / laneCount}% - 7px)`,
  };
  const title = `${displayInstant(start, timeZone)}–${displayInstant(end, timeZone)} · ${entry.registrationMark ?? 'Sem aeronave'} · ${entry.originName} → ${entry.destinationName}`;
  return (
    <button
      type="button"
      className={`calendarEvent ${eventTone(entry)}`}
      style={style}
      title={title}
      aria-label={`Abrir ${entry.referenceCode}: ${title}`}
      onClick={() => onOpen(entry.operationId)}
    >
      <strong>
        {displayInstant(start, timeZone)} · {entry.registrationMark ?? entry.referenceCode}
      </strong>
      <span>{entry.missionName || `${entry.originName} → ${entry.destinationName}`}</span>
      <small>
        {entry.originName} → {entry.destinationName}
      </small>
    </button>
  );
}

function CalendarGrid({
  days,
  entries,
  utc,
  timeZone,
  onOpen,
  onCreate,
}: {
  readonly days: readonly string[];
  readonly entries: readonly AgendaEntry[];
  readonly utc: boolean;
  readonly timeZone: string;
  readonly onOpen: (id: string) => void;
  readonly onCreate?: ((preset: OperationSchedulePreset) => void) | undefined;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const today = calendarDate(new Date(), utc);
  const now = new Date();
  const nowMinute =
    (utc ? now.getUTCHours() : now.getHours()) * 60 +
    (utc ? now.getUTCMinutes() : now.getMinutes());
  const layouts = useMemo(
    () => new Map(days.map((day) => [day, calendarEventLayout(entries, day, utc)])),
    [days, entries, utc],
  );

  useEffect(() => {
    const viewport = scrollRef.current;
    if (!viewport) return;
    const targetMinute = days.includes(today) ? Math.max(0, nowMinute - 120) : 6 * 60;
    viewport.scrollTop = (targetMinute / 60) * hourHeight;
  }, [days, today, nowMinute]);

  const createAt = (day: string, hour: number, event: MouseEvent<HTMLButtonElement>) => {
    if (!onCreate) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const minute =
      hour * 60 + (event.detail > 0 && event.clientY - bounds.top >= bounds.height / 2 ? 30 : 0);
    onCreate(calendarSlot(day, minute, utc));
  };
  const calendarStyle = {
    '--calendar-days': days.length,
    '--calendar-min-width': days.length === 1 ? '420px' : '1040px',
    '--calendar-hour-height': `${hourHeight}px`,
  } as CSSProperties;

  return (
    <div
      className="calendarScroll"
      ref={scrollRef}
      role="region"
      aria-label="Grade da agenda de voos"
      tabIndex={0}
    >
      <div className="calendarFrame" style={calendarStyle}>
        <div className="calendarHeader">
          <div className="calendarZone">{utc ? 'UTC' : 'Local'}</div>
          {days.map((day) => {
            const label = dayLabel(day);
            return (
              <div
                className={
                  day === today ? 'calendarDayHeader calendarDayHeader-today' : 'calendarDayHeader'
                }
                key={day}
              >
                <span>{label.weekday}</span>
                <strong>{label.number}</strong>
                <small>{layouts.get(day)?.length ?? 0} voo(s)</small>
              </div>
            );
          })}
        </div>
        <div className="calendarBody">
          <div className="calendarHours" aria-hidden="true">
            {hours.map((hour) => (
              <span key={hour} style={{ top: hour * hourHeight - 7 }}>
                {String(hour).padStart(2, '0')}:00
              </span>
            ))}
          </div>
          {days.map((day) => (
            <div
              className={day === today ? 'calendarDay calendarDay-today' : 'calendarDay'}
              key={day}
            >
              {hours.map((hour) => (
                <button
                  type="button"
                  className="calendarSlot"
                  disabled={!onCreate}
                  key={hour}
                  style={{ top: hour * hourHeight, height: hourHeight }}
                  aria-label={
                    onCreate
                      ? `Criar operação em ${day}, às ${String(hour).padStart(2, '0')}:00 ou 30 minutos`
                      : `Horário ${String(hour).padStart(2, '0')}:00`
                  }
                  onClick={(event) => createAt(day, hour, event)}
                >
                  {onCreate ? <span>+ Criar operação</span> : null}
                </button>
              ))}
              {day === today ? (
                <div
                  className="calendarNow"
                  style={{ top: (nowMinute / 60) * hourHeight }}
                  aria-label="Horário atual"
                >
                  <span />
                </div>
              ) : null}
              {layouts.get(day)?.map((item) => (
                <CalendarEvent key={item.entry.id} {...item} timeZone={timeZone} onOpen={onOpen} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const monthWeekdays = ['seg', 'ter', 'qua', 'qui', 'sex', 'sáb', 'dom'];

function MonthCalendar({
  days,
  selectedDate,
  entries,
  utc,
  timeZone,
  onOpen,
  onCreate,
  onSelectDay,
}: {
  readonly days: readonly string[];
  readonly selectedDate: string;
  readonly entries: readonly AgendaEntry[];
  readonly utc: boolean;
  readonly timeZone: string;
  readonly onOpen: (id: string) => void;
  readonly onCreate?: ((preset: OperationSchedulePreset) => void) | undefined;
  readonly onSelectDay: (day: string) => void;
}) {
  const today = calendarDate(new Date(), utc);
  const selectedMonth = selectedDate.slice(0, 7);
  const entriesByDay = useMemo(
    () =>
      new Map(
        days.map((day) => [
          day,
          entriesForDay(entries, day, utc).toSorted(
            (left, right) =>
              Date.parse(left.revisedDeparture ?? left.plannedDeparture) -
              Date.parse(right.revisedDeparture ?? right.plannedDeparture),
          ),
        ]),
      ),
    [days, entries, utc],
  );

  return (
    <div className="monthCalendar" role="grid" aria-label="Agenda mensal de voos">
      {monthWeekdays.map((weekday) => (
        <div className="monthWeekday" role="columnheader" key={weekday}>
          {weekday}
        </div>
      ))}
      {days.map((day) => {
        const dayEntries = entriesByDay.get(day) ?? [];
        const outsideMonth = day.slice(0, 7) !== selectedMonth;
        return (
          <div
            className={`monthDay${outsideMonth ? ' monthDay-outside' : ''}${day === today ? ' monthDay-today' : ''}`}
            role="gridcell"
            key={day}
            aria-label={`${day}, ${dayEntries.length} voo(s)`}
          >
            <button
              type="button"
              className="monthDayNumber"
              aria-label={`Abrir agenda diária de ${day}`}
              onClick={() => onSelectDay(day)}
            >
              {Number(day.slice(8))}
            </button>
            <div className="monthEvents">
              {dayEntries.slice(0, 3).map((entry) => {
                const start = entry.revisedDeparture ?? entry.plannedDeparture;
                return (
                  <button
                    type="button"
                    className={`monthEvent ${eventTone(entry)}`}
                    key={entry.id}
                    title={`${entry.referenceCode} · ${entry.originName} → ${entry.destinationName}`}
                    onClick={() => onOpen(entry.operationId)}
                  >
                    <strong>{displayInstant(start, timeZone)}</strong>
                    <span>{entry.registrationMark ?? entry.referenceCode}</span>
                  </button>
                );
              })}
              {dayEntries.length > 3 ? (
                <button type="button" className="monthMore" onClick={() => onSelectDay(day)}>
                  +{dayEntries.length - 3} voo(s)
                </button>
              ) : null}
            </div>
            {onCreate && !outsideMonth ? (
              <button
                type="button"
                className="monthCreate"
                onClick={() => onCreate(calendarSlot(day, 8 * 60, utc))}
                aria-label={`Criar operação em ${day}`}
              >
                + Operação
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export function OperationAgenda({
  organizationId,
  active,
  onOpenOperation,
  onCreate,
  onExpiredSession,
}: {
  readonly organizationId: string;
  readonly active: boolean;
  readonly onOpenOperation: (id: string) => void;
  readonly onCreate?: ((preset?: OperationSchedulePreset) => void) | undefined;
  readonly onExpiredSession: () => Promise<void>;
}) {
  const [utc, setUtc] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 900px)').matches,
  );
  useEffect(() => {
    const media = window.matchMedia('(min-width: 900px)');
    const changed = () => setFiltersOpen(media.matches);
    media.addEventListener('change', changed);
    return () => media.removeEventListener('change', changed);
  }, []);
  const [date, setDate] = useState(() => calendarDate(new Date(), false));
  const [view, setView] = useState<CalendarView>(() =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 900px)').matches
      ? 'week'
      : 'day',
  );
  const [aircraftId, setAircraftId] = useState('');
  const [baseId, setBaseId] = useState('');
  const [state, setState] = useState('');
  const [data, setData] = useState<AgendaResponse>();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [revision, setRevision] = useState(0);
  const { days, from, to } = useMemo(() => calendarPeriod(date, view, utc), [date, view, utc]);
  const timeZone = utc ? 'UTC' : Intl.DateTimeFormat().resolvedOptions().timeZone;
  const movePeriod = (direction: -1 | 1) =>
    setDate((current) =>
      view === 'month'
        ? moveCalendarMonth(current, direction)
        : moveCalendarDate(current, direction * (view === 'week' ? 7 : 1)),
    );

  useEffect(() => {
    if (!active) return;
    const controller = new AbortController();
    setLoading(true);
    setError('');
    const params = new URLSearchParams({
      from,
      to,
      ...(aircraftId ? { aircraftId } : {}),
      ...(baseId ? { baseId } : {}),
      ...(state ? { state } : {}),
    });
    void fetch(`/v1/organizations/${organizationId}/agenda?${params}`, {
      credentials: 'include',
      signal: controller.signal,
    })
      .then(async (response) => {
        if (response.status === 401) {
          await onExpiredSession();
          return;
        }
        if (!response.ok) {
          const body = (await response.json()) as { code: string };
          throw new Error(
            scheduleErrors[body.code] ?? 'Não foi possível carregar a agenda. Tente novamente.',
          );
        }
        const value = (await response.json()) as AgendaResponse;
        if (!controller.signal.aborted) setData(value);
      })
      .catch((caught: unknown) => {
        if (!controller.signal.aborted)
          setError(
            caught instanceof Error ? caught.message : 'Não foi possível carregar a agenda.',
          );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [organizationId, active, from, to, aircraftId, baseId, state, revision, onExpiredSession]);

  const entries = data?.entries ?? [];
  const operationCount = new Set(entries.map((entry) => entry.operationId)).size;
  const conflictCount = new Set(
    entries
      .filter(
        (entry) => entry.conflicts.length && !entry.cancelledAt && entry.state !== 'cancelled',
      )
      .map((entry) => entry.operationId),
  ).size;

  return (
    <section className="agendaWorkspace" aria-labelledby="agenda-title" aria-busy={loading}>
      <header className="boardHeading agendaHeading">
        <div>
          <span className="sectionEyebrow">Centro de controle operacional</span>
          <h2 id="agenda-title" tabIndex={-1}>
            Agenda de voos
          </h2>
          <p>Veja ocupações e espaços livres. Clique em um horário vago para programar um voo.</p>
        </div>
        {onCreate ? (
          <button type="button" className="primaryButton" onClick={() => onCreate()}>
            + Nova operação
          </button>
        ) : null}
      </header>
      <div className="agendaControls">
        <div className="agendaPeriodControls">
          <button type="button" aria-label="Período anterior" onClick={() => movePeriod(-1)}>
            ←
          </button>
          <button type="button" onClick={() => setDate(calendarDate(new Date(), utc))}>
            Hoje
          </button>
          <button type="button" aria-label="Próximo período" onClick={() => movePeriod(1)}>
            →
          </button>
          <label>
            <span>Data</span>
            <input
              type="date"
              value={date}
              min="2000-01-01"
              max="2100-12-31"
              onChange={(event) => {
                if (
                  /^\d{4}-\d{2}-\d{2}$/.test(event.target.value) &&
                  Number.isFinite(Date.parse(event.target.value))
                )
                  setDate(event.target.value);
              }}
            />
          </label>
        </div>
        <div className="agendaViewControls" role="group" aria-label="Visualização da agenda">
          <button type="button" aria-pressed={view === 'day'} onClick={() => setView('day')}>
            Dia
          </button>
          <button type="button" aria-pressed={view === 'week'} onClick={() => setView('week')}>
            Semana
          </button>
          <button type="button" aria-pressed={view === 'month'} onClick={() => setView('month')}>
            Mês
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={() => setRevision((current) => current + 1)}
          >
            Atualizar
          </button>
        </div>
      </div>
      <details className="agendaFiltersDisclosure" open={filtersOpen}>
        <summary
          onClick={(event) => {
            event.preventDefault();
            setFiltersOpen((open) => !open);
          }}
        >
          Filtros da agenda{aircraftId || baseId || state ? ' · filtros aplicados' : ''}
        </summary>
        <div className="agendaFilters">
          <label>
            <span id="agenda-aircraft-label">Aeronave</span>
            <select
              aria-labelledby="agenda-aircraft-label"
              value={aircraftId}
              onChange={(event) => setAircraftId(event.target.value)}
            >
              <option value="">Todas as aeronaves</option>
              {data?.aircraft.map((aircraft) => (
                <option key={aircraft.id} value={aircraft.id}>
                  {aircraft.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span id="agenda-base-label">Base</span>
            <select
              aria-labelledby="agenda-base-label"
              value={baseId}
              onChange={(event) => setBaseId(event.target.value)}
            >
              <option value="">Todas as bases</option>
              {data?.bases.map((base) => (
                <option key={base.id} value={base.id}>
                  {base.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span id="agenda-state-label">Estado</span>
            <select
              aria-labelledby="agenda-state-label"
              value={state}
              onChange={(event) => setState(event.target.value)}
            >
              <option value="">Exceto canceladas</option>
              {Object.entries(scheduleStates).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span id="agenda-zone-label">Horários exibidos em</span>
            <select
              aria-labelledby="agenda-zone-label"
              value={utc ? 'utc' : 'local'}
              onChange={(event) => setUtc(event.target.value === 'utc')}
            >
              <option value="local">{Intl.DateTimeFormat().resolvedOptions().timeZone}</option>
              <option value="utc">UTC</option>
            </select>
          </label>
        </div>
      </details>
      <div className="agendaSummary" role="status">
        {loading
          ? 'Carregando agenda…'
          : `${operationCount} operação(ões) · ${entries.length} trecho(s) · ${conflictCount} com conflitos`}
        <span>
          <i className="agendaLegendDot agendaLegendDot-ready" /> Pronto{' '}
          <i className="agendaLegendDot agendaLegendDot-attention" /> Atenção · {timeZone}
        </span>
      </div>
      {error ? (
        <div role="alert" className="workflowError">
          {error}{' '}
          <button type="button" onClick={() => setRevision((current) => current + 1)}>
            Tentar novamente
          </button>
        </div>
      ) : null}
      {data?.possiblyTruncated ? (
        <p role="alert" className="agendaAttention">
          Há muitos trechos neste período. Exibindo até 500; reduza o período ou filtre por
          aeronave/base.
        </p>
      ) : null}
      {!loading && !error ? (
        <>
          {entries.length === 0 ? (
            <div className="agendaEmptyCompact">
              <strong>Agenda livre neste período.</strong>
              <span>
                {onCreate
                  ? ' Clique em qualquer horário para criar a primeira operação.'
                  : ' Não há voos programados.'}
              </span>
            </div>
          ) : null}
          {view === 'month' ? (
            <MonthCalendar
              days={days}
              selectedDate={date}
              entries={entries}
              utc={utc}
              timeZone={timeZone}
              onOpen={onOpenOperation}
              onCreate={onCreate ? (preset) => onCreate(preset) : undefined}
              onSelectDay={(day) => {
                setDate(day);
                setView('day');
              }}
            />
          ) : (
            <CalendarGrid
              days={days}
              entries={entries}
              utc={utc}
              timeZone={timeZone}
              onOpen={onOpenOperation}
              onCreate={onCreate ? (preset) => onCreate(preset) : undefined}
            />
          )}
          <p className="agendaLegend">
            Os blocos mostram a duração planejada de cada trecho. Voos simultâneos aparecem lado a
            lado; conflitos, pendências e estados operacionais são destacados por cor.
          </p>
        </>
      ) : null}
    </section>
  );
}
